<template>
  <div id="pulsePage">
    <!-- Pulse top -->
    <PulseResults
      v-if="['desktop', 'laptop'].includes($mq)"
      :pulse-report="pulseReport"
      :show-type="showType"
    />

    <!-- Mobile button -->
    <PulseButtons
      v-if="['mobile', 'tablet'].includes($mq)"
      :show-type="showType"
    />

    <!-- Pulse Tab -->
    <CustomTabs
      :id="`pulse_${selectedPulse}Tab`"
      :default-tab.sync="defaultTab"
    >
      <template v-for="(lPulse, keyName, keyPulse) in localPulse">
        <!-- Tab Title -->
        <div
          :slot="`title${keyPulse + 1}`"
          :key="`pulseWrapTitle${keyPulse}`"
          :class="`colouredTitle ${keyName}`"
          @click="selectedPulse = keyPulse + 1"
        >
          <div
            v-if="$mq === 'laptop' || $mq === 'desktop'"
            class="title"
          >
            {{ keyName | pulseName }}
          </div>
        </div>

        <!-- Tab Contents -->
        <div
          :slot="`content${keyPulse + 1}`"
          :key="`pulseWrapContent${keyPulse}`"
          :loading="loading"
        >
          <el-row
            v-if="lPulse"
            class="contentRow defaultCursor"
            :class="['desktop', 'laptop'].includes($mq) ? 'deskModeRow' : 'mobileModeRow'"
            type="flex"
          >
            <div
              v-if="['mobile', 'tablet'].includes($mq)"
              class="mobileTitle"
              :class="`pulse${keyPulse + 1}`"
            >
              {{ keyName | pulseName }}
            </div>

            <!-- Contents - Response status -->
            <el-col
              class="contentCol statusCol"
              :span="responsiveCol.one"
            >
              <div class="subTitle">
                Response Status
              </div>
              <PulseStatus
                :pulse-each="lPulse"
                :pulse-name="keyName"
                :pulse-number="keyPulse"
              />
            </el-col>

            <!-- Contents - Reminder -->
            <el-col
              class="contentCol reminderCol"
              :span="responsiveCol.two"
            >
              <div class="subTitle">
                Schedule
              </div>
              <PulseSteps
                :pulse="lPulse"
                :pulse-number="keyPulse + 1"
                @updatePulse="onUpdatePulse"
              />
            </el-col>

            <!-- Contents - Calendar -->
            <el-col
              v-if="['desktop', 'laptop'].includes($mq)"
              class="contentCol calendarCol"
              :span="responsiveCol.three"
            >
              <div
                v-if="lPulse"
                class="pulseCalendarWrap"
              >
                <PulseCalendar
                  :pulse-date="formattedDate[keyName]"
                  :pulse-number="keyPulse + 1"
                />
              </div>
            </el-col>
          </el-row>

          <div
            v-else
            class="noData"
            :class="['desktop', 'laptop'].includes($mq) ? 'deskModeRow' : 'mobileModeRow'"
          >
            <div
              v-if="['mobile', 'tablet'].includes($mq)"
              class="mobileTitle"
              :class="`pulse${keyPulse + 1}`"
            >
              {{ keyName | pulseName }}
            </div>
            <span class="pulseNoData">
              {{ keyName | pulseName }} data
              will be displayed after
              Session {{ (keyPulse + (7 + (2 * keyPulse - 1))) }} meeting
            </span>
          </div>
        </div>
      </template>
    </CustomTabs>

    <RespondentTable
      v-if="showCoworkers"
      :key="`Respondent_${selectedPulse}`"
      :pulse.sync="pulse"
      :activated-pulse="selectedPulse"
    />
  </div>
</template>

<script>
import CustomTabs from '@/views/components/customTabs/CustomTabs.vue';
import PulseResults from './pulseWidget/PulseResults.vue';
import PulseStatus from './pulseWidget/PulseStatus.vue';
import PulseCalendar from './pulseWidget/PulseCalendar.vue';
import PulseSteps from './pulseWidget/PulseSteps.vue';
import PulseButtons from './pulseWidget/PulseButtons.vue';
import RespondentTable from './pulseWidget/RespondentTable.vue';

export default {
  components: {
    PulseResults,
    CustomTabs,
    PulseStatus,
    PulseCalendar,
    PulseSteps,
    PulseButtons,
    RespondentTable,
  },

  filters: {
    pulseName(pulseId) {
      switch (pulseId) {
        case 'pulse1':
          return 'Pulse I';
        case 'pulse2':
          return 'Pulse II';
        case 'pulse3':
          return 'Pulse III';
        default:
          return '';
      }
    },
  },

  props: {
    pulse: {
      type: Object,
      default: () => ({
        pulse1: null,
      }),
    },

    pulseReport: {
      type: Object,
      default: null,
    },

    showCoworkers: {
      type: Boolean,
      default: false,
    },

    censorCheck: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedPulse: 1,
      defaultTab: '0',
    };
  },

  computed: {
    responsiveCol() {
      const col = {};

      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.one = 24;
        col.two = 24;
      } else {
        col.one = 6;
        col.two = 5;
        col.three = 13;
      }

      return col;
    },

    formattedDate() {
      const wrap = {};

      if (this.localPulse) {
        Object.keys(this.localPulse).forEach((item) => {
          if (this.localPulse[item]) {
            const dateList = [
              this.localPulse[item].reminder1_date,
              this.localPulse[item].reminder2_date,
              this.localPulse[item].reminder3_date,
            ].filter((el) => el);

            let option = '';
            if (this.$moment(this.localPulse[item].start_date).format('MM')
              !== this.$moment(this.localPulse[item].close_date).format('MM')) {
              option = ['start', 'end'];
            } else {
              option = ['end'];
            }

            wrap[item] = {
              start_date: this.localPulse[item].start_date,
              close_date: this.localPulse[item].close_date,
              dateList,
              option,
            };
          }
        });
      }
      return wrap;
    },

    localPulse: {
      get() {
        if (!this.pulse || !this.pulseReport?.leader?.duration) return { pulse1: null };

        const pulse = {};
        if (typeof this.pulse.pulse1 !== 'undefined') {
          pulse.pulse1 = this.pulse.pulse1;
        }

        if (typeof this.pulse.pulse2 !== 'undefined' && this.pulseReport.leader.duration > 7) {
          pulse.pulse2 = this.pulse.pulse2;
        }

        if (typeof this.pulse.pulse3 !== 'undefined' && this.pulseReport.leader.duration > 10) {
          pulse.pulse3 = this.pulse.pulse3;
        }
        return pulse;
      },
      set(data) {
        this.$emit('update:pulse', data);
      },
    },

    showType() {
      let type = '';
      if (this.$route?.name) {
        type = this.$route?.name;
      }
      return type;
    },
  },

  watch: {
    responsiveCol: {
      handler(newValue) {
        if (newValue.one === 24) {
          this.getDefaultTab();
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.getDefaultTab();
  },

  methods: {
    onUpdatePulse(data) {
      this.$emit('update:pulse', data);
      this.$message.success('Pulse status successfully updated');
    },

    getDefaultTab() {
      Object.keys(this.pulse).forEach((el) => {
        const key = parseInt(el.replace('pulse', ''), 10);
        if (this.pulse[`pulse${key}`] && Object.entries(this.pulse[`pulse${key}`]).length > 0) {
          this.selectedPulse = key;
          this.defaultTab = `${key - 1}`;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  #pulsePage {
    .noData {
      padding: 203px 0;
      text-align: center;
      color: #c4c4c4;
      font-style: italic;
    }

    .expandBtn {
      width: 100%;
      background: transparent;
      color: var(--themeFontColor);
      word-break: break-word;
      white-space: normal;
      padding: 9px 5px;

      &.disabledBtn {
        background: rgba(255, 255, 255, 0.3);
        color: #c5c5c5;
      }
    }

    .reportBtnRow {
      .viewReportBtn {
        word-break: break-word;
        white-space: normal;
        padding: 13px 5px;
      }

      &.deskMode {
        padding: 25px 0 5px;

        .viewReportBtn {
          width: 65%;
        }
      }

      &.mobileMode {
        padding: 25px 0 25px;

        .viewReportBtn {
          width: 100%;
        }
      }

      .el-col {
        text-align: center;
      }
    }

    .customTabs {
      .el-tabs__item {
        .colouredTitle {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .title {
          padding: 10px 0;
          font-weight: bold;
        }
      }

      .el-tabs__content {
        .subTitle {
          padding: 0 0 15px;
          font-size: 14px;
          font-weight: bold;
        }

        .contentRow {
          align-items: stretch;

          .contentCol {
            padding: 10px 15px;
            height: 420px;

            &.statusCol {
              padding-left: 0;
            }

            &.reminderCol {
              padding-right: 0;
            }

            &.calendarCol {
              height: 367px;
              padding: 0 5px 0 0;
              margin: 43px 0 15px 10px;
              border-top: 1px solid var(--themeFontColor);
              border-bottom: 1px solid var(--themeFontColor);
            }

            // Col scroll bar style
            scrollbar-color: #c8c8c8 transparent;
            overflow-y: scroll; /* has to be scroll, not auto */
            -webkit-overflow-scrolling: touch;

            &::-webkit-scrollbar {
              width: 4px;
            }

            &::-webkit-scrollbar-thumb {
              background: #c8c8c8;
              border-radius: 20px;
            }

            &::-webkit-scrollbar-track {
              background: transparent !important;
            }
          }
        }
      }

      .mobileModeRow {
        position: relative;
        flex-wrap: wrap;
        background: var(--themeCardColor);
        margin-bottom: 20px;
        border-radius: 15px;
        overflow: hidden;
        padding: 80px 15px 15px;
        color: var(--themeFontColor);

        .reminderCol {
          padding-top: 20px;
        }

        .mobileTitle {
          position: absolute;
          top: 0;
          left: 0;
          padding: 20px 0;
          box-sizing: border-box;
          width: 100%;
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          font-style: normal;

          &.pulse1 {
            background: #f3c065;
          }

          &.pulse2 {
            background: #3aaace;
          }

          &.pulse3 {
            background: #b79eff;
          }
        }

        .subTitle {
          margin: 0 0 15px;
          font-weight: bold;
        }

        .el-col {
          margin: 0;
          border-right: 0 !important;
        }

        &.noData {
          padding: 110px 15px 45px;

          .pulseNoData {
            color: #c4c4c4;
            font-style: italic;
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #pulsePage {
    .customTabs {
      .el-tabs__item {
        height: 60px;
        position: relative;

        &:not(:first-child) {
          margin-left: 10px;
        }
      }

      .el-tabs__content {
        background: transparent;
        padding: 35px;
      }

      // Pulse color 1
      &#pulse_1Tab {
        .el-tabs__item.is-active {
          background: #f3c065;
        }

        .el-tabs__content {
          border: 4px solid #f3c065;
        }

        .contentCol {
          &:first-child {
            border-right: 2px solid #f3c065;
          }
        }
      }

      // Pulse color 2
      &#pulse_2Tab {
        .el-tabs__item.is-active {
          background: #3aaace;
        }

        .el-tabs__content {
          border: 4px solid #3aaace;
        }

        .contentRow {
          .contentCol {
            &:first-child {
              border-right: 2px solid #3aaace;
            }
          }
        }
      }

      // Pulse color 3
      &#pulse_3Tab {
        .el-tabs__item.is-active {
          background: #b79eff;
        }

        .el-tabs__content {
          border: 4px solid #b79eff;
        }

        .contentRow {
          .contentCol {
            &:first-child {
              border-right: 2px solid #b79eff;
            }
          }
        }
      }
    }
  }
</style>
